import React, { createContext, useCallback, useContext, useEffect } from 'react';
import { ethers, parseUnits, JsonRpcProvider, ContractRunner } from 'ethers';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { Lending, Lending__factory } from '@gu-corp/lending-contracts';
// Component
import { useAccount } from 'wagmi';
import { getEnv } from '~/env';
import { LendingPlanEnum } from '~/enums/lending-plan.enum';
import {
  useSeedContractStateLazyQuery,
  useSeedLendingRecordsLazyQuery,
} from '~/graphql/lending-seed-subgraph/types';
import {
  useSproutContractStateLazyQuery,
  useSproutLendingRecordsLazyQuery,
} from '~/graphql/lending-sprout-subgraph/types';
import {
  useHarvestContractStateLazyQuery,
  useHarvestLendingRecordsLazyQuery,
} from '~/graphql/lending-harvest-subgraph/types';
import { getLendingUsdtxReward } from '~/utils/lending.util';

const LIMIT_RECORDS_PER_CALL = 1000;

export type LendingRecord = {
  id: string;
  recordId: string;
  lender: string;
  amount: string;
  lended: {
    blockTimestamp: string;
    blockNumber: string;
    transactionHash: string;
  };
  withdrawn: {
    id: string;
    transactionHash: string;
    lender: string;
  } | null;
  rewardAmount: string;
  plan: LendingPlanEnum;
  createdAt: number;
  releasedAt: number;
  contract: (runner?: ContractRunner) => Lending;
};

export type LendingContractData = {
  duration: bigint; // Duration of the lending in seconds
  minimumLoanAmount: bigint; // Minimum JOC tokens that can be deposited (12)
  numeratorRewardRate: bigint; // Numerator of the reward rate
  denominatorRewardRate: bigint; // Denominator of the reward rate
  balance: bigint; // JOC tokens in the contract
  totalRewardTokens: bigint; // Total USDTX tokens that can be distributed
  decimalConversationRate: bigint; // Decimal conversion rate between JOC and USDTX. (1 JOC = 10^decimalConversionRate USDTX) (12)
  maximumDepositTokens?: bigint; // Maximum JOC tokens that can be deposited
  remainingDepositTokens: bigint; // Remaining JOC tokens that can be deposited
  totalDepositedTokens?: bigint; // Total JOC tokens that have been deposited
  contract: (runner?: ContractRunner) => Lending;
};

export type LendingData = LendingContractData & {
  records: Array<LendingRecord>;
};

export const getLendingContractData = async (
  plan: LendingPlanEnum,
): Promise<LendingContractData> => {
  const contractAddress =
    plan === LendingPlanEnum.SEED
      ? getEnv('NEXT_PUBLIC_SEED_CONTRACT_ADDRESS')
      : plan === LendingPlanEnum.SPROUT
      ? getEnv('NEXT_PUBLIC_SPROUT_CONTRACT_ADDRESS')
      : getEnv('NEXT_PUBLIC_HARVEST_CONTRACT_ADDRESS');

  const jocUrl = getEnv('NEXT_PUBLIC_JOC_URL');
  if (!contractAddress || !jocUrl) {
    throw new Error('Missing required environment variables for lending contract');
  }

  const provider: JsonRpcProvider = new ethers.JsonRpcProvider(jocUrl);
  const contract = Lending__factory.connect(contractAddress, provider);

  const duration = await contract.duration();
  const minimumLoanAmount = await contract.MINIMUM_LOAN_AMOUNT();
  const [numeratorRewardRate, denominatorRewardRate] = await contract.rewardRate();
  const balance = await contract.getBalance();
  const totalRewardTokens = await contract.totalRewardTokens();
  const decimalConversationRate = await contract.decimalConversationRate();
  const remainingDepositTokens = parseUnits(
    ((totalRewardTokens * denominatorRewardRate) / numeratorRewardRate).toString(),
    decimalConversationRate,
  );

  return {
    duration,
    minimumLoanAmount,
    numeratorRewardRate,
    denominatorRewardRate,
    balance,
    totalRewardTokens,
    decimalConversationRate,
    remainingDepositTokens,
    contract: (runner: ContractRunner = provider) =>
      Lending__factory.connect(contractAddress, runner),
  };
};

export type LendingDataContextType = {
  seed: LendingData | null;
  seedError: boolean;
  refetchSeed: () => void;
  sprout: LendingData | null;
  sproutError: boolean;
  refetchSprout: () => void;
  harvest: LendingData | null;
  harvestError: boolean;
  refetchHarvest: () => void;
};

const LendingDataContext = createContext<LendingDataContextType>({
  seed: null,
  seedError: false,
  sprout: null,
  sproutError: false,
  harvest: null,
  harvestError: false,
  refetchSeed: () => {},
  refetchSprout: () => {},
  refetchHarvest: () => {},
});

export const withLendingData = <P extends Record<string, unknown>>(
  ChildComponent: React.ComponentType<P>,
) => {
  const WithLendingDataHOC = (props: P) => {
    const [seed, setSeed] = React.useState<LendingData | null>(null);
    const [seedError, setSeedError] = React.useState<boolean>(false);
    const [sprout, setSprout] = React.useState<LendingData | null>(null);
    const [sproutError, setSproutError] = React.useState<boolean>(false);
    const [harvest, setHarvest] = React.useState<LendingData | null>(null);
    const [harvestError, setHarvestError] = React.useState<boolean>(false);
    const { address, status } = useAccount();

    const [fetchSeedRecords] = useSeedLendingRecordsLazyQuery();
    const [fetchSproutRecords] = useSproutLendingRecordsLazyQuery();
    const [fetchHarvestRecords] = useHarvestLendingRecordsLazyQuery();

    const [fetchSeedContractState] = useSeedContractStateLazyQuery();
    const [fetchSproutContractState] = useSproutContractStateLazyQuery();
    const [fetchHarvestContractState] = useHarvestContractStateLazyQuery();

    const fetchSeedPlanData = useCallback(async () => {
      try {
        setSeedError(false);
        const contractStateResult = await fetchSeedContractState({
          variables: { id: getEnv('NEXT_PUBLIC_SEED_CONTRACT_ADDRESS') || '' },
          fetchPolicy: 'network-only',
        });

        if (contractStateResult.error) {
          console.error('Failed to fetch seed contract state:', contractStateResult.error);
          setSeedError(true);
          return;
        }

        const totalDepositedTokens = contractStateResult.data?.seed?.totalLended || 0;
        const seedContractData = await getLendingContractData(LendingPlanEnum.SEED);

        const maximumDepositTokens =
          BigInt(totalDepositedTokens) + BigInt(seedContractData.remainingDepositTokens);

        const recordsResult = await fetchSeedRecords({
          variables: { lender: address, first: LIMIT_RECORDS_PER_CALL },
          fetchPolicy: 'network-only',
        });

        if (recordsResult.error) {
          console.error('Failed to fetch seed lending records:', recordsResult.error);
          setSeedError(true);
          return;
        }

        const records = (recordsResult.data?.seed || []).map((item) => ({
          ...item,
          id: `${LendingPlanEnum.SEED}_${item.id}`,
          lended: {
            blockTimestamp: item.lended.blockTimestamp,
            blockNumber: item.lended.blockNumber,
            transactionHash: item.lended.transactionHash,
          },
          withdrawn: item.withdrawn
            ? {
                id: item.withdrawn.id,
                transactionHash: item.withdrawn.transactionHash,
                lender: item.withdrawn.lender,
              }
            : null,
          plan: LendingPlanEnum.SEED,
          rewardAmount: getLendingUsdtxReward(seedContractData, item.amount).toString(),
          maximumDepositTokens: maximumDepositTokens,
          contract: seedContractData.contract,
          createdAt: Number(item.lended.blockTimestamp),
          releasedAt: Number(item.lended.blockTimestamp) + Number(seedContractData.duration),
        }));

        setSeed({
          ...seedContractData,
          maximumDepositTokens: maximumDepositTokens,
          totalDepositedTokens: totalDepositedTokens,
          records,
        });
      } catch (error) {
        console.error('Failed to fetch seed plan data:', error);
        setSeedError(true);
      }
    }, [fetchSeedRecords, fetchSeedContractState, address]);

    const fetchSproutPlanData = useCallback(async () => {
      try {
        setSproutError(false);
        const contractStateResult = await fetchSproutContractState({
          variables: { id: getEnv('NEXT_PUBLIC_SPROUT_CONTRACT_ADDRESS') || '' },
          fetchPolicy: 'network-only',
        });

        if (contractStateResult.error) {
          console.error('Failed to fetch sprout contract state:', contractStateResult.error);
          setSproutError(true);
          return;
        }

        const totalDepositedTokens = contractStateResult.data?.sprout?.totalLended || 0;
        const sproutContractData = await getLendingContractData(LendingPlanEnum.SPROUT);

        const maximumDepositTokens =
          BigInt(totalDepositedTokens) + BigInt(sproutContractData.remainingDepositTokens);

        const recordsResult = await fetchSproutRecords({
          variables: { lender: address, first: LIMIT_RECORDS_PER_CALL },
          fetchPolicy: 'network-only',
        });

        if (recordsResult.error) {
          console.error('Failed to fetch sprout lending records:', recordsResult.error);
          setSproutError(true);
          return;
        }

        const records = (recordsResult.data?.sprout || []).map((item) => ({
          ...item,
          id: `${LendingPlanEnum.SPROUT}_${item.id}`,
          lended: {
            blockTimestamp: item.lended.blockTimestamp,
            blockNumber: item.lended.blockNumber,
            transactionHash: item.lended.transactionHash,
          },
          withdrawn: item.withdrawn
            ? {
                id: item.withdrawn.id,
                transactionHash: item.withdrawn.transactionHash,
                lender: item.withdrawn.lender,
              }
            : null,
          plan: LendingPlanEnum.SPROUT,
          rewardAmount: getLendingUsdtxReward(sproutContractData, item.amount).toString(),
          maximumDepositTokens: maximumDepositTokens,
          contract: sproutContractData.contract,
          createdAt: Number(item.lended.blockTimestamp),
          releasedAt: Number(item.lended.blockTimestamp) + Number(sproutContractData.duration),
        }));

        setSprout({
          ...sproutContractData,
          maximumDepositTokens: maximumDepositTokens,
          totalDepositedTokens: totalDepositedTokens,
          records,
        });
      } catch (error) {
        console.error('Failed to fetch sprout plan data:', error);
        setSproutError(true);
      }
    }, [fetchSproutRecords, fetchSproutContractState, address]);

    const fetchHarvestPlanData = useCallback(async () => {
      try {
        setHarvestError(false);
        const contractStateResult = await fetchHarvestContractState({
          variables: { id: getEnv('NEXT_PUBLIC_HARVEST_CONTRACT_ADDRESS') || '' },
          fetchPolicy: 'network-only',
        });

        if (contractStateResult.error) {
          console.error('Failed to fetch harvest contract state:', contractStateResult.error);
          setHarvestError(true);
          return;
        }

        const totalDepositedTokens = contractStateResult.data?.harvest?.totalLended || 0;
        const harvestContractData = await getLendingContractData(LendingPlanEnum.HARVEST);

        const maximumDepositTokens =
          BigInt(totalDepositedTokens) + BigInt(harvestContractData.remainingDepositTokens);

        const recordsResult = await fetchHarvestRecords({
          variables: { lender: address, first: LIMIT_RECORDS_PER_CALL },
          fetchPolicy: 'network-only',
        });

        if (recordsResult.error) {
          console.error('Failed to fetch harvest lending records:', recordsResult.error);
          setHarvestError(true);
          return;
        }

        const records = (recordsResult.data?.harvest || []).map((item) => ({
          ...item,
          id: `${LendingPlanEnum.HARVEST}_${item.id}`,
          lended: {
            blockTimestamp: item.lended.blockTimestamp,
            blockNumber: item.lended.blockNumber,
            transactionHash: item.lended.transactionHash,
          },
          withdrawn: item.withdrawn
            ? {
                id: item.withdrawn.id,
                transactionHash: item.withdrawn.transactionHash,
                lender: item.withdrawn.lender,
              }
            : null,
          plan: LendingPlanEnum.HARVEST,
          rewardAmount: getLendingUsdtxReward(harvestContractData, item.amount).toString(),
          maximumDepositTokens: maximumDepositTokens,
          contract: harvestContractData.contract,
          createdAt: Number(item.lended.blockTimestamp),
          releasedAt: Number(item.lended.blockTimestamp) + Number(harvestContractData.duration),
        }));

        setHarvest({
          ...harvestContractData,
          maximumDepositTokens: maximumDepositTokens,
          totalDepositedTokens: totalDepositedTokens,
          records,
        });
      } catch (error) {
        console.error('Failed to fetch harvest plan data:', error);
        setHarvestError(true);
      }
    }, [fetchHarvestRecords, fetchHarvestContractState, address]);

    useEffect(() => {
      fetchSeedPlanData();
      fetchSproutPlanData();
      fetchHarvestPlanData();
    }, [fetchSeedPlanData, fetchSproutPlanData, fetchHarvestPlanData]);

    return (
      <LendingDataContext.Provider
        value={{
          seed,
          seedError,
          refetchSeed: fetchSeedPlanData,
          sprout,
          sproutError,
          refetchSprout: fetchSproutPlanData,
          harvest,
          harvestError,
          refetchHarvest: fetchHarvestPlanData,
        }}
      >
        <ChildComponent {...props} />
      </LendingDataContext.Provider>
    );
  };

  WithLendingDataHOC.displayName = 'WithLendingDataHOC';
  hoistNonReactStatics(WithLendingDataHOC, ChildComponent);

  return WithLendingDataHOC;
};

export const useLendingData = () => {
  return useContext(LendingDataContext);
};

export const useLendingPlanData = (plan: LendingPlanEnum) => {
  const {
    seed,
    seedError,
    refetchSeed,
    sprout,
    sproutError,
    refetchSprout,
    harvest,
    harvestError,
    refetchHarvest,
  } = useContext(LendingDataContext);

  switch (plan) {
    case LendingPlanEnum.SEED:
      return { data: seed, error: seedError, refetch: refetchSeed };
    case LendingPlanEnum.SPROUT:
      return { data: sprout, error: sproutError, refetch: refetchSprout };
    case LendingPlanEnum.HARVEST:
      return { data: harvest, error: harvestError, refetch: refetchHarvest };
    default:
      return { data: seed, error: seedError, refetch: refetchSeed };
  }
};
